import React, { useRef, useState, useCallback } from 'react';
import Webcam from 'react-webcam';
import { Button, Stack, Text, Group, Box } from '@mantine/core';
import { IconCamera, IconRefresh, IconDeviceFloppy } from '@tabler/icons-react';
import { createStyles } from '@mantine/styles';

const useStyles = createStyles((theme) => ({
  webcamContainer: {
    width: '100%',
    maxWidth: '500px',
    margin: '0 auto',
    borderRadius: 'var(--border-radius)',
    overflow: 'hidden',
    boxShadow: 'var(--shadow-md)',
    transition: 'var(--transition)',
    border: '4px solid white',
    filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.05))',
    '[data-theme="dark"] &': {
      border: '4px solid var(--bg-card)',
      filter: 'drop-shadow(0 10px 15px rgba(0, 0, 0, 0.2))',
    },
    '&:hover': {
      transform: 'translateY(-5px)',
      boxShadow: 'var(--shadow-lg)',
    },
  },
  buttonWrapper: {
    width: '100%',
    maxWidth: '500px',
    display: 'flex',
    justifyContent: 'center',
    margin: '24px auto 0',
  },
  captureButton: {
    background:
      'linear-gradient(45deg, var(--primary-color), var(--secondary-color))',
    color: 'white',
    fontWeight: 600,
    '&:hover': {
      background:
        'linear-gradient(45deg, var(--primary-hover), var(--secondary-color))',
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-md)',
    },
    border: 'none',
    height: '50px',
    padding: '0 32px',
    transition: 'var(--transition)',
    borderRadius: '25px',
    display: 'inline-flex',
    alignItems: 'center',
  },
  actionButton: {
    height: '48px',
    fontWeight: 500,
    padding: '0 24px',
    borderRadius: '24px',
    transition: 'var(--transition)',
    display: 'inline-flex',
    alignItems: 'center',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: 'var(--shadow-sm)',
    },
  },
  successButton: {
    background: 'linear-gradient(45deg, var(--success-color), #38bdf8)',
    color: 'white',
    fontWeight: 600,
    border: 'none',
    '&:hover': {
      background: 'linear-gradient(45deg, #22c55e, #0ea5e9)',
      boxShadow: 'var(--shadow-md)',
    },
  },
  capturedImage: {
    width: '100%',
    maxWidth: '500px',
    borderRadius: 'var(--border-radius)',
    boxShadow: 'var(--shadow-md)',
    transition: 'var(--transition)',
    border: '4px solid white',
    '[data-theme="dark"] &': {
      border: '4px solid var(--bg-card)',
    },
    animation: 'fadeIn 0.5s ease-out',
    display: 'block',
    margin: '0 auto',
  },
  placeholderContainer: {
    width: '100%',
    maxWidth: '500px',
    height: '375px',
    margin: '0 auto',
    backgroundColor: 'rgba(255, 255, 255, 0.5)',
    backdropFilter: 'blur(8px)',
    borderRadius: 'var(--border-radius)',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    boxShadow: 'var(--shadow-sm)',
    border: '3px dashed rgba(203, 213, 225, 0.8)',
    transition: 'var(--transition)',
    cursor: 'pointer',
    '[data-theme="dark"] &': {
      backgroundColor: 'rgba(30, 41, 59, 0.5)',
      border: '3px dashed rgba(71, 85, 105, 0.8)',
    },
    '&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.8)',
      borderColor: 'var(--primary-color)',
      transform: 'translateY(-5px)',
      '[data-theme="dark"] &': {
        backgroundColor: 'rgba(30, 41, 59, 0.8)',
      },
    },
  },
  placeholderIcon: {
    color: 'var(--text-light)',
    marginBottom: '16px',
    transition: 'var(--transition)',
  },
  buttonContainer: {
    margin: '24px auto 0',
    width: '100%',
    maxWidth: '500px',
    display: 'flex',
    justifyContent: 'center',
    gap: '16px',
  },
}));

const WebcamCapture = ({ onImageCapture }) => {
  const { classes } = useStyles();
  const webcamRef = useRef(null);
  const [isCaptureEnabled, setIsCaptureEnabled] = useState(false);
  const [capturedImage, setCapturedImage] = useState(null);

  const enableCapture = () => {
    setCapturedImage(null);
    setIsCaptureEnabled(true);
  };

  const capture = useCallback(() => {
    if (webcamRef.current) {
      const imageSrc = webcamRef.current.getScreenshot();
      setCapturedImage(imageSrc);
      setIsCaptureEnabled(false);
    }
  }, [webcamRef]);

  const retake = () => {
    setCapturedImage(null);
    setIsCaptureEnabled(true);
  };

  const confirmImage = () => {
    if (capturedImage && onImageCapture) {
      onImageCapture(capturedImage);
    }
  };

  const videoConstraints = {
    width: 500,
    height: 500,
    facingMode: 'user',
  };

  return (
    <Stack align="center" spacing="md" style={{ width: '100%' }}>
      {isCaptureEnabled ? (
        <>
          <div className={classes.webcamContainer}>
            <Webcam
              audio={false}
              ref={webcamRef}
              screenshotFormat="image/jpeg"
              videoConstraints={videoConstraints}
              width="100%"
              height="100%"
            />
          </div>
          <Box className={classes.buttonWrapper}>
            <Button
              leftSection={<IconCamera size={20} />}
              onClick={capture}
              className={classes.captureButton}
            >
              Capture Photo
            </Button>
          </Box>
        </>
      ) : capturedImage ? (
        <>
          <div
            style={{ width: '100%', display: 'flex', justifyContent: 'center' }}
          >
            <img
              src={capturedImage}
              alt="Captured"
              className={classes.capturedImage}
            />
          </div>
          <Group
            position="center"
            spacing="md"
            className={classes.buttonContainer}
          >
            <Button
              leftSection={<IconRefresh size={18} />}
              variant="outline"
              color="gray"
              onClick={retake}
              className={classes.actionButton}
            >
              Retake
            </Button>
            <Button
              leftSection={<IconDeviceFloppy size={18} />}
              onClick={confirmImage}
              className={`${classes.actionButton} ${classes.successButton}`}
            >
              Use This Photo
            </Button>
          </Group>
        </>
      ) : (
        <>
          <div className={classes.placeholderContainer} onClick={enableCapture}>
            <IconCamera size={64} className={classes.placeholderIcon} />
            <Text
              color="var(--text-secondary)"
              size="xl"
              align="center"
              px="md"
              weight={500}
            >
              Camera is disabled
            </Text>
            <Text
              color="var(--text-light)"
              size="sm"
              align="center"
              px="md"
              mt="12px"
            >
              Click to enable your camera and take a photo
            </Text>
          </div>
          <Box className={classes.buttonWrapper}>
            <Button
              leftSection={<IconCamera size={20} />}
              onClick={enableCapture}
              className={classes.captureButton}
            >
              Enable Camera
            </Button>
          </Box>
        </>
      )}
    </Stack>
  );
};

export default WebcamCapture;
